import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Copy, Trash2, FileSpreadsheet, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { qgApi, errorMessage } from "@/lib/qgApi";
import { formatINR, centsFromStr } from "@/lib/qgCalc";
import { PdfActions } from "@/components/quotation-generator/PdfActions";
import { CreateQuotationDialog, NumberPromptDialog } from "@/components/quotation-generator/QuotationDialogs";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function StatusPill({ q }) {
  if (q.status === "final") return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Issued</Badge>;
  return <Badge className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50">Draft</Badge>;
}

function Row({ q, onOpen, onDuplicate, onDelete }) {
  const p = q.progress || {};
  const done = (p.complete || 0) + (p.not_applicable || 0);
  return (
    <tr className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50/70 dark:hover:bg-slate-800/60 transition-colors" data-testid={`qg-row-${q.id}`}>
      <td className="px-4 py-3">
        <button onClick={onOpen} className="font-mono text-sm font-semibold text-slate-900 dark:text-white hover:text-brillien-600 inline-flex items-center gap-1" data-testid={`qg-open-${q.id}`}>
          {q.quotation_number}<ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </td>
      <td className="px-4 py-3"><p className="text-sm font-medium text-slate-900 dark:text-white">{q.client_name}</p><p className="text-xs text-slate-500">{q.project_name}</p></td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 max-w-[180px] truncate" title={q.location}>{q.location || "—"}</td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">{q.quotation_date || "—"}</td>
      <td className="px-4 py-3 text-sm font-mono text-slate-700 dark:text-slate-300">{q.version_label}</td>
      <td className="px-4 py-3"><StatusPill q={q} /><p className="text-[11px] text-slate-400 mt-1">{done}/12 sections done</p></td>
      <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-slate-900 dark:text-white whitespace-nowrap" data-testid={`qg-total-${q.id}`}>{formatINR(centsFromStr(q.total_boq), true)}</td>
      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(q.updated_at)}{q.created_by_name && <p className="text-[11px] text-slate-400" data-testid={`qg-created-by-${q.id}`}>by {q.created_by_name}</p>}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 justify-end">
          <PdfActions compact quotationId={q.id} fileName={`${q.quotation_number}_${q.version_label}`} testPrefix={`qg-list-pdf-${q.id}`} />
          <Button variant="outline" size="sm" onClick={onDuplicate} title="Duplicate" data-testid={`qg-duplicate-${q.id}`} className="rounded-lg"><Copy className="h-4 w-4" /></Button>
          {!q.issued_revisions?.length && (
            <Button variant="outline" size="sm" onClick={onDelete} title="Delete draft" data-testid={`qg-delete-${q.id}`} className="rounded-lg text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function QuotationGeneratorPage() {
  const navigate = useNavigate();
  const { user, hasModuleAccess } = useAuth();
  const allowed = !user || hasModuleAccess("quotation-generator");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [dupFor, setDupFor] = useState(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try { setRows(await qgApi.list(q)); } catch (e) { toast.error(errorMessage(e, "Failed to load quotations")); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!allowed) return undefined;
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search, load, allowed]);

  if (!allowed) {
    return <div className="p-8" data-testid="qg-admin-only"><p className="text-slate-700 dark:text-slate-200">You don't have access to the Quotation Generator. Ask an Admin to assign it to you in Settings → Users.</p></div>;
  }

  const create = async (body) => {
    try {
      const q = await qgApi.create(body);
      toast.success("Quotation created");
      navigate(`/quotation-generator/${q.id}`);
    } catch (e) { throw new Error(errorMessage(e, "Could not create quotation")); }
  };
  const duplicate = async (number) => {
    try {
      const q = await qgApi.duplicate(dupFor.id, number);
      toast.success("Quotation duplicated");
      setDupFor(null);
      navigate(`/quotation-generator/${q.id}`);
    } catch (e) { throw new Error(errorMessage(e, "Could not duplicate")); }
  };
  const remove = async (q) => {
    if (!window.confirm(`Delete draft ${q.quotation_number}? This cannot be undone.`)) return;
    try { await qgApi.remove(q.id); toast.success("Draft deleted"); load(search); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen" data-testid="quotation-generator-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brillien-600 mb-1">Design &amp; Build</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Quotation Generator</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Build BOQ quotations from the Brillen template and issue professional PDFs.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="qg-new-quotation-btn" className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium">
          <Plus className="h-4 w-4 mr-2" />New quotation
        </Button>
      </div>

      <div className="relative max-w-md mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search quotation number, client or project" className="pl-9 rounded-lg bg-white dark:bg-slate-800" data-testid="qg-search-input" />
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
        <table className="w-full min-w-[1100px]" data-testid="qg-list-table">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-[11px] uppercase tracking-wider text-slate-500">
              {["Quotation no.", "Client / project", "Location", "Date", "Version", "Status", "Total BOQ value", "Last updated", ""].map((h, i) => (
                <th key={h || i} className={`px-4 py-3 font-semibold ${i === 6 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <Row key={q.id} q={q} onOpen={() => navigate(`/quotation-generator/${q.id}`)} onDuplicate={() => setDupFor(q)} onDelete={() => remove(q)} />
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="py-16 text-center" data-testid="qg-empty-state">
            <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">{search ? "No quotations match your search" : "No quotations yet"}</p>
            <p className="text-xs text-slate-400 mt-1">Create one to start from the 12-section Brillen BOQ template.</p>
          </div>
        )}
        {loading && rows.length === 0 && <div className="py-12 text-center text-sm text-slate-400">Loading…</div>}
      </div>

      <CreateQuotationDialog open={createOpen} onOpenChange={setCreateOpen} onCreate={create} defaultPreparedBy={user?.name} />
      <NumberPromptDialog open={!!dupFor} onOpenChange={(o) => !o && setDupFor(null)} title={`Duplicate ${dupFor?.quotation_number || ""}`} onSubmit={duplicate} />
    </div>
  );
}
